import React, { useState, useEffect } from 'react';
import { Modal, Form, Input, Space, InputNumber, Button, Select, message } from 'antd';
import { PlusOutlined, DeleteOutlined, MinusCircleOutlined } from '@ant-design/icons';
import { CreateSankalpa } from '../types';
import { sankalpaTagAPI } from '../services/api';

const { TextArea } = Input;
const { Option } = Select;

interface AddSankalpaModalProps {
  visible: boolean;
  onCancel: () => void;
  onSubmit: (values: CreateSankalpa) => Promise<void>;
}

const AddSankalpaModal: React.FC<AddSankalpaModalProps> = ({
  visible,
  onCancel,
  onSubmit
}) => {
  const [form] = Form.useForm();
  const [allTags, setAllTags] = useState<any[]>([]);
  const [loadingTags, setLoadingTags] = useState(false);
  const [creatingTag, setCreatingTag] = useState(false);
  const [searchValue, setSearchValue] = useState('');
  const [activeTagFieldName, setActiveTagFieldName] = useState<number | null>(null);

  useEffect(() => {
    if (visible) {
      loadAllTags();
    }
  }, [visible]);

  const loadAllTags = async () => {
    setLoadingTags(true);
    try {
      const response = await sankalpaTagAPI.getAll();
      setAllTags(response.data);
    } catch (error) {
      console.error('Error loading tags:', error);
    } finally {
      setLoadingTags(false);
    }
  };

  const createTag = async (name: string) => {
    try {
      const response = await sankalpaTagAPI.createTag({ name });
      setAllTags(prev => [...prev, response.data]);
      return response.data.id;
    } catch (error) {
      console.error('Error creating tag:', error);
      message.error('Failed to create tag');
      return null;
    }
  };

  const handleSearch = (value: string) => {
    setSearchValue(value);
  };

  const handleCreateTag = async (fieldName?: number) => {
    const fieldToSet = fieldName !== undefined ? fieldName : activeTagFieldName;
    if (searchValue.trim()) {
      setCreatingTag(true);
      const tagId = await createTag(searchValue.trim());
      if (tagId !== null && fieldToSet !== null) {
        form.setFieldValue(['tagIds', fieldToSet], tagId);
      }
      setSearchValue('');
      setCreatingTag(false);
      setActiveTagFieldName(null);
    }
  };

  const handleCancel = () => {
    form.resetFields();
    setSearchValue('');
    onCancel();
  };

  const handleSubmit = async (values: any) => {
    console.log('Modal handleSubmit called with values:', values);
    const processedValues = {
      ...values,
      tagIds: values.tagIds?.filter((id: any) => id !== undefined) || []
    };
    console.log('Processed values:', processedValues);
    await onSubmit(processedValues);
    form.resetFields();
    setSearchValue('');
  };

  return (
    <Modal
      title="Add Sankalpa"
      open={visible}
      onCancel={handleCancel}
      onOk={() => form.submit()}
      width={600}
    >
      <Form form={form} layout="vertical" onFinish={handleSubmit}>
        <Form.Item label="Name" name="name" rules={[{ required: true, message: 'Please enter sankalpa name' }]}>
          <Input placeholder="Enter sankalpa name" />
        </Form.Item>
        <Form.Item label="Tags">
          <Form.List name="tagIds">
            {(fields, { add, remove }) => (
              <>
                {fields.map(({ key, name, ...restField }) => (
                  <div key={key} style={{ display: 'flex', marginBottom: 8 }}>
                    <Form.Item
                      {...restField}
                      name={name}
                      style={{ marginBottom: 0, flex: 1 }}
                    >
                      <Select
                        showSearch
                        placeholder="Search or create tag"
                        loading={loadingTags}
                        onSearch={handleSearch}
                        filterOption={(input, option) =>
                          (option?.label as string)?.toLowerCase().includes(input.toLowerCase())
                        }
                        onFocus={() => setActiveTagFieldName(name)}
                        notFoundContent={
                          searchValue ? (
                            <Button
                              type="text"
                              icon={<PlusOutlined />}
                              onClick={() => handleCreateTag(name)}
                              loading={creatingTag}
                              block
                            >
                              Create "{searchValue}"
                            </Button>
                          ) : null
                        }
                        options={allTags.map(tag => ({
                          label: tag.name,
                          value: tag.id
                        }))}
                      />
                    </Form.Item>
                    <Button
                      onClick={() => remove(name)}
                      style={{ marginLeft: 8 }}
                      danger
                      icon={<MinusCircleOutlined />}
                    />
                  </div>
                ))}
                <Button type="dashed" onClick={() => add()} block icon={<PlusOutlined />}>
                  Add Tag
                </Button>
              </>
            )}
          </Form.List>
        </Form.Item>
        <Form.List name="lines">
          {(fields, { add, remove }) => (
            <>
              {fields.map(({ key, name, ...restField }, index) => (
                <Space key={key} style={{ display: 'flex', marginBottom: 8, width: '100%' }} align="baseline">
                  <Form.Item
                    {...restField}
                    name={[name, 'content']}
                    rules={[{ required: true, message: 'Missing content' }]}
                    style={{ flex: 1 }}
                  >
                    <TextArea placeholder="Line content" rows={2} />
                  </Form.Item>
                  <Form.Item {...restField} name={[name, 'order']} initialValue={index + 1}>
                    <InputNumber placeholder="Order" min={1} readOnly value={index + 1} />
                  </Form.Item>
                  <Button onClick={() => remove(name)} icon={<DeleteOutlined />} />
                </Space>
              ))}
              <Form.Item>
                <Button type="dashed" onClick={() => add()} block icon={<PlusOutlined />}>
                  Add Line
                </Button>
              </Form.Item>
            </>
          )}
        </Form.List>
      </Form>
    </Modal>
  );
};

export default AddSankalpaModal;